import { useEffect, useRef, useState, type MouseEvent } from 'react'
import type { ChatThread } from '@shared/services'

/**
 * Which of the project's conversations is open (Huanrui, 2026-10-22).
 *
 * A pill at the top left of the conversation: its name and a chevron. The
 * chevron opens a list -- "New chat" first, then the project's conversations,
 * the newest first -- and picking one opens it. A new one is only kept once
 * its first message is sent (docs/chat-threads.sql). Double-clicking the name
 * renames the conversation in place (Enter keeps it, Escape leaves it); the
 * first one is "Main chat" until it is renamed (docs/chat-main-title.sql).
 *
 * `current` is '' for the project's first conversation, 'new' for one not
 * sent yet, otherwise a thread's id. A new one can be named before its first
 * message (Huanrui, 2026-11-02): `newName` is shown, and kept with it when
 * it is sent (ProjectPage).
 */
export function ThreadPicker({
  threads,
  current,
  onOpen,
  onRename,
  newName = '',
  disabled = false,
}: {
  threads: ChatThread[]
  current: string
  onOpen: (thread: string) => void
  /** Absent until the project exists. For 'new', the name is kept until the first message makes the conversation. */
  onRename?: (thread: string, title: string) => Promise<void>
  /** The name given to a new conversation not sent yet. */
  newName?: string
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const canRename = Boolean(onRename)
  /** A click on the name, held back for a moment in case it is the first of a double-click. */
  const clickTimer = useRef<number | null>(null)
  useEffect(() => () => {
    if (clickTimer.current) window.clearTimeout(clickTimer.current)
  }, [])

  /*
   * THE WHOLE PILL OPENS THE LIST (Huanrui, 2026-11-03), not only the
   * chevron; a double-click on the name still renames. A click on the name
   * waits 250 ms before opening, so that a double-click never opens and shuts
   * the list on its way to renaming. Anywhere else on the pill, and Enter on
   * the chevron (a click with no count), opens at once.
   */
  const pillClick = (event: MouseEvent<HTMLDivElement>) => {
    if (renaming || disabled) return
    if (clickTimer.current) {
      window.clearTimeout(clickTimer.current)
      clickTimer.current = null
    }
    // The second click of a double-click: the rename takes it.
    if (event.detail > 1) return
    const onName = (event.target as HTMLElement).closest('.ww-threads__name')
    if (!onName || !canRename || event.detail === 0) {
      setOpen((was) => !was)
      return
    }
    clickTimer.current = window.setTimeout(() => {
      clickTimer.current = null
      setOpen((was) => !was)
    }, 250)
  }

  useEffect(() => {
    if (!open) return
    const away = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false)
    }
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('pointerdown', away)
    window.addEventListener('keydown', key)
    return () => {
      window.removeEventListener('pointerdown', away)
      window.removeEventListener('keydown', key)
    }
  }, [open])

  const nameOf = (thread: ChatThread) => thread.title || (thread.id === null ? 'Main chat' : 'Untitled chat')
  const here = current === 'new' ? null : threads.find((one) => (one.id ?? '') === current)
  const label = current === 'new' ? newName || 'New chat' : here ? nameOf(here) : 'Main chat'

  const finishRename = (value: string) => {
    setRenaming(false)
    const title = value.trim()
    // Unchanged, or emptied on one that must have a name: nothing to do.
    if (!onRename || title === label || (!title && current !== '' && current !== 'new')) return
    void onRename(current, title)
  }
  // Newest first; the project's first conversation, the oldest, at the end.
  const listed = [...threads].reverse()

  return (
    <div ref={box} className={`ww-threads${open ? ' ww-threads--open' : ''}`}>
      <div
        className={`ww-threads__pill${renaming ? ' ww-threads__pill--renaming' : ''}${disabled ? ' ww-threads__pill--disabled' : ''}`}
        onClick={pillClick}
      >
        {renaming ? (
          <input
            className="ww-threads__input"
            defaultValue={label}
            autoFocus
            maxLength={120}
            size={Math.max(6, Math.min(label.length + 1, 28))}
            onFocus={(event) => event.currentTarget.select()}
            onBlur={(event) => finishRename(event.currentTarget.value)}
            onKeyDown={(event) => {
              event.stopPropagation()
              if (event.key === 'Enter') event.currentTarget.blur()
              if (event.key === 'Escape') setRenaming(false)
            }}
            aria-label="Conversation name"
          />
        ) : (
          <span
            className="ww-threads__name"
            onDoubleClick={() => {
              if (!canRename) return
              if (clickTimer.current) {
                window.clearTimeout(clickTimer.current)
                clickTimer.current = null
              }
              setOpen(false)
              setRenaming(true)
            }}
            title={canRename ? `${label} (double-click to rename)` : label}
          >
            {label}
          </span>
        )}
        <button
          type="button"
          className="ww-threads__toggle"
          // Opened by the pill round it (pillClick), keyboard included.
          disabled={disabled}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Conversations"
          title={disabled ? 'Wait for the answer to finish' : 'Switch or start a conversation'}
        >
          <svg className="ww-threads__chevron" viewBox="0 0 12 12" width="11" height="11" aria-hidden="true">
            <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      {open ? (
        <div className="ww-threads__menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className={`ww-threads__item ww-threads__item--new${current === 'new' ? ' ww-threads__item--on' : ''}`}
            onClick={() => {
              setOpen(false)
              onOpen('new')
            }}
          >
            <svg viewBox="0 0 14 14" width="13" height="13" aria-hidden="true">
              <path d="M7 2.5v9M2.5 7h9" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            New chat
          </button>
          <div className="ww-threads__rule" />
          <div className="ww-threads__list">
            {listed.map((thread) => {
              const id = thread.id ?? ''
              const on = id === current
              return (
                <button
                  key={id || 'first'}
                  type="button"
                  role="menuitemradio"
                  aria-checked={on}
                  className={`ww-threads__item${on ? ' ww-threads__item--on' : ''}`}
                  onClick={() => {
                    setOpen(false)
                    onOpen(id)
                  }}
                  title={nameOf(thread)}
                >
                  <span className="ww-threads__item-name">{nameOf(thread)}</span>
                  {on ? (
                    <svg viewBox="0 0 14 14" width="12" height="12" aria-hidden="true">
                      <path d="M2.5 7.5 5.5 10.5 11.5 4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : null}
                </button>
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}
